import { Entity } from '../types/entity';
import { Dictionary } from '../../../../types/dictionary';
import * as SyncedRemoteEntityStore from './synced-remote-entity.store';
import * as ProgressStore from '../../remote/progress.store';
import { handleProgressState } from '../../remote/progress.store';
import { createAction, createGetter, on, select } from '../../../factories';
import { StoreFeature } from '../../../store';
import { handleSubscriptionState } from '../../remote/subscription.store';
import { SOFTLINE_STORE_ENTITY_SERVICE } from './entity.shared';
import { SOFTLINE_SERVICE_UUID } from '../../../../core.shared';
import {
  ActionState,
  handleObservableActionState,
} from '../../remote/action.store';
import { lastValueFrom } from 'rxjs';

export interface UploadActionParameters<T extends object = object> {
  entity: T;
  pathParams?: Dictionary<unknown>;
  token?: string;
}

export interface State<T extends Entity = Entity>
  extends SyncedRemoteEntityStore.State<T>,
    ProgressStore.State {}

export class Store<T extends Entity = Entity> {
  mutations = {
    ...this.entityStore.mutations,
    progress: ProgressStore.mutations,
  };

  actions = {
    ...this.entityStore.actions,
    upload: createAction<State<T>, UploadActionParameters, T>('upload'),
  };

  getters = {
    ...this.entityStore.getters,
    progress: ProgressStore.getters,
    uploading: createGetter<State<T>, boolean>('uploading'),
    uploaded: createGetter<State<T>, boolean>('uploaded'),
  };

  feature: StoreFeature<State<T>> = {
    initialState: {
      ...this.entityStore.feature.initialState,
      ...ProgressStore.feature.initialState,
    },
    mutations: [
      ...this.entityStore.feature.mutations,
      ...ProgressStore.feature.mutations,
    ],
    getters: [
      ...this.entityStore.feature.getters,
      ...ProgressStore.feature.getters,
      select(this.getters.uploading, ({ get, featureName, params }) => {
        const actions = [this.actions.upload.name];
        const states: ActionState[] = ['pending', 'processing'];
        return get(featureName, getters.action.hasState, {
          actions,
          states,
          id: params,
        });
      }),
      select(this.getters.uploaded, ({ get, featureName, params }) => {
        const actions = [this.actions.upload.name];
        const states: ActionState[] = ['succeeded', 'failed'];
        return get(featureName, getters.action.hasState, {
          actions,
          states,
          id: params,
        });
      }),
    ],
    actions: [
      ...this.entityStore.feature.actions,
      on(
        this.actions.upload,
        async ({ commit, featureName, params, injector }) => {
          const service = injector.get(SOFTLINE_STORE_ENTITY_SERVICE);
          const token = params.token ?? injector.get(SOFTLINE_SERVICE_UUID)();

          const progress$ = service.upload(params.entity, params.pathParams);
          const result$ = handleProgressState(
            progress$,
            featureName,
            commit,
            token
          );
          const subscription$ = handleSubscriptionState(
            result$,
            featureName,
            commit,
            token
          );
          const result = await lastValueFrom(
            handleObservableActionState(
              subscription$,
              featureName,
              commit,
              this.actions.upload.name,
              token
            )
          );
          commit(featureName, this.mutations.set, result);
          return result;
        }
      ),
    ],
  };

  constructor(private entityStore: SyncedRemoteEntityStore.Store<T>) {}
}

export function create<T extends Entity>(): Store<T> {
  const entityStore = SyncedRemoteEntityStore.create<T>();
  return new Store<T>(entityStore);
}

const instance = create();
export const mutations = instance.mutations;
export const getters = instance.getters;
export const actions = instance.actions;
export const feature = instance.feature;
